import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const FavoritesContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const fetchFavorites = useCallback(async () => {
    if (!user) { setFavorites(new Set()); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/favorites`);
      setFavorites(new Set(res.data.map(f => f.product_title)));
    } catch { setFavorites(new Set()); }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  const toggleFavorite = async (productTitle) => {
    if (!user) return { needsAuth: true };

    const isFav = favorites.has(productTitle);
    // Optimistic update
    setFavorites(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(productTitle); else next.add(productTitle);
      return next;
    });

    try {
      if (isFav) {
        await axios.delete(`${API}/favorites/${encodeURIComponent(productTitle)}`);
      } else {
        await axios.post(`${API}/favorites/${encodeURIComponent(productTitle)}`);
      }
      return { success: true, added: !isFav };
    } catch {
      // Revert on error
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFav) next.add(productTitle); else next.delete(productTitle);
        return next;
      });
      return { success: false };
    }
  };

  const isFavorite = (productTitle) => favorites.has(productTitle);

  return (
    <FavoritesContext.Provider value={{ favorites, loading, toggleFavorite, isFavorite, count: favorites.size, refresh: fetchFavorites }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
};
